import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as api from '../api/client';

const AUTH_KEY = 'formfit_admin_auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
    localStorage.removeItem(AUTH_KEY);
  }, []);

  useEffect(() => {
    api.setUnauthorizedHandler(logout);
  }, [logout]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = localStorage.getItem(AUTH_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          if (data?.admin) setUser(data.admin);
        }
        const res = await api.refresh();
        if (cancelled) return;
        if (res?.admin) {
          setUser(res.admin);
          localStorage.setItem(AUTH_KEY, JSON.stringify({ admin: res.admin }));
        } else {
          setUser(null);
          localStorage.removeItem(AUTH_KEY);
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          localStorage.removeItem(AUTH_KEY);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.login(email, password);
    if (res?.admin) {
      setUser(res.admin);
      localStorage.setItem(AUTH_KEY, JSON.stringify({ admin: res.admin }));
      return true;
    }
    return false;
  }, []);

  const isAuthenticated = !!user;

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    ready,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
